import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CountryService } from '../../core/services/country-services';
import { AddressFormData, FormDataService } from '../../core/services/form-data-service';
import { RecaptchaComponent } from 'ng-recaptcha';
import { TextService } from '../../core/services/text-service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-shared-address-form',
  templateUrl: './shared-address-form.html',
  styleUrls: ['./shared-address-form.scss'],
  standalone: false
})
export class SharedAddressForm implements OnInit {
  environment = environment;
  @ViewChild('captchaRef') captcha!: RecaptchaComponent;
  private captchaResolver?: (token: string | null) => void;
  private captchaToken: string | null = null;

  form!: FormGroup;
  countries: any[] = [];
  states: any[] = [];
  selectedCountry: any = null;


  constructor(
    private fb: FormBuilder,
    private countryService: CountryService,
    private formData: FormDataService,
    public textService: TextService
  ) {

  }

  ngOnInit(): void {
    this.form = this.fb.group({
      street: ['', Validators.required],
      number: [''],
      country: ['', Validators.required],
      state: [{ value: '', disabled: true }, Validators.required],
      city: ['', Validators.required],
      postalCode: [''],
    });

    this.countryService.getCountries().subscribe(res => (this.countries = res));
    this.form.get('country')!.valueChanges.subscribe(code => {
      this.onCountryChange(code);
    });


    const saved = this.formData.getAddressData();
    if (saved) {
      this.form.patchValue(saved);
      if (saved.country) {
        this.onCountryChange(saved.country);
      }
    }
  }

  ngAfterViewInit() {
    // RESET LISTENER
    this.formData.captchaReset$.subscribe(() => {
      if (this.isCaptchaInDom()) {
        this.captcha.reset();
      }
    });

    // EXECUTE LISTENER
    this.formData.captchaExecute$.subscribe(() => {
      if (this.captcha) {
        this.executeCaptcha();
      }
    });
  }

  executeCaptcha(): Promise<string | null> {
    return new Promise(resolve => {
  
      if (!this.isCaptchaInDom()) {
        console.warn('Captcha not in DOM — skip execute');
        resolve(null);
        return;
      }
  
      this.captchaResolver = resolve;
      this.captcha.execute();
    });
  }

  onCaptchaResolved(token: string | null) {
    // console.log('[SharedAddressForm] Captcha resolved:', token);
  
    if (!token) return;
  
    this.captchaToken = token;
    this.formData.setCaptchaToken(token);
  
    if (this.captchaResolver) {
      this.captchaResolver(token);
      this.captchaResolver = undefined;
    }
  }

  private isCaptchaInDom(): boolean {
    return !!document.querySelector('#captcha-container re-captcha');
  }
  
  getCaptchaToken(): string | null {
    return this.captchaToken;
  }


  private onCountryChange(countryCode: string): void {
    this.states = [];
    this.form.get('state')!.reset();
    this.form.get('state')!.disable();

    this.selectedCountry = this.countryService
      .getCachedCountries()
      .find((c: any) => c.value === countryCode);

    if (countryCode) {
      this.countryService.getStates(countryCode).subscribe(states => {
        this.states = states;
        if (this.states.length) {
          this.form.get('state')!.enable();


          const saved = this.formData.getAddressData();
          if (saved?.state) {
            this.form.get('state')!.setValue(saved.state);
          }
        }
      });
    }


    const postal = this.form.get('postalCode')!;
    postal.clearValidators();
    if (!this.selectedCountry || this.selectedCountry.hasZipCode) {
      postal.setValidators([Validators.required]);
    }
    postal.updateValueAndValidity();
  }

  getValue(): AddressFormData | null {
    this.form.updateValueAndValidity();
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return null;
    }
    return this.form.value as AddressFormData;
  }

  markAllTouched() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
  }

  fillMockAddress() {
    const mockCountry = 'US';
    const mockState = 'CA';

    this.form.patchValue({
      street: '123 Demo Street',
      number: '45B',
      country: mockCountry,
      city: 'San Francisco',
      postalCode: '94105'
    });

    this.countryService.getStates(mockCountry).subscribe(states => {
      this.states = states;
      if (this.states.find(s => s.value === mockState)) {
        this.form.get('state')!.enable();
        this.form.get('state')!.setValue(mockState);
      }
    });

    this.form.markAllAsTouched();
  }

}
